import { NavTabs } from "@/ds/custom/NavTabs";
import { StatePanel } from "@/ds/custom/StatePanel";
import { useCollection } from "@/features/collections/api/useCollection";
import { HeaderBar } from "@/features/common/components/HeaderBar";
import { CircleOff } from "lucide-react";
import { useMemo, type FC } from "react";
import { Outlet, useLocation, useParams } from "react-router";

type CollectionTab = "summary" | "edit" | "trends" | "tools" | "settings";

export const PageCollection: FC = () => {
	const params = useParams();
	const collectionId = params.collectionId as string;

	const collection = useCollection(collectionId);
	const title = useMemo(() => collection.data?.name || "Collection", [collection.data]);
	const description = useMemo(() => collection.data?.description || "", [collection.data]);

	const breadcrumb = useMemo(() => [
		{ label: "Dashboard", href: "/dashboard" },
		{ label: "Collections", href: "/collections" },
		{ label: collection.data?.name || "Collection" }
	], [collection.data]);

	const location = useLocation();

	const activeTab = useMemo((): CollectionTab | null => {
		if (location.pathname.endsWith(`/${collectionId}`)) return "summary";
		if (location.pathname.endsWith("/trends")) return "trends";
		if (location.pathname.endsWith("/edit")) return "edit";
		if (location.pathname.includes("/transactions/") && !location.pathname.endsWith("/bulk")) return "edit";
		if (location.pathname.endsWith("/tools")) return "tools";
		if (location.pathname.endsWith("/settings")) return "settings";
		return null;
	}, [location.pathname, collectionId]);

	const tabs = useMemo(() => {
		const base = `/collections/${collectionId}`;
		return [
			{ label: "Summary", to: base, active: activeTab === "summary" },
			{ label: "Trends", to: `${base}/trends`, active: activeTab === "trends" },
			{ label: "Edit", to: `${base}/edit`, active: activeTab === "edit" },
			{ label: "Tools", to: `${base}/tools`, active: activeTab === "tools" },
			{ label: "Settings", to: `${base}/settings`, active: activeTab === "settings" }
		];
	}, [collectionId, activeTab]);

	if (!collectionId) {
		return (
			<>
				<HeaderBar title={title} description={description} breadcrumb={breadcrumb} />

				<StatePanel
					icon={CircleOff}
					title="Collection Not Found"
					description="We could not find the collection you selected." />
			</>
		);
	}

	return (
		<>
			<HeaderBar title={title} description={description} breadcrumb={breadcrumb} />

			{
				activeTab &&
				<div className="p-3 border-b">
					<NavTabs tabs={tabs} />
				</div>
			}

			<Outlet />
		</>
	);
};
